import React, { forwardRef } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface DateRangePickerProps {
  label?: string;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  onChange: (dates: [Date | null, Date | null]) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  touched?: boolean;
  showResetIcon?: boolean;
  onReset?: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label = "Select Date",
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  error,
  touched,
  showResetIcon = false,
  onReset,
}) => {
  const CustomInput = forwardRef<
    HTMLInputElement,
    { value?: string; onClick?: () => void }
  >(({ value, onClick }, ref) => (
    <div className="form-floating position-relative">
      <input
        type="text"
        className={`form-control ${touched && error ? "is-invalid" : ""}`}
        onClick={onClick}
        ref={ref}
        value={value || ""}
        placeholder=" "
        readOnly
        id="datepicker-input"
        disabled={disabled}
      />

      <label>
        {label}
        {!disabled && required && <span style={{ color: "red" }}> *</span>}
      </label>

      {/* Reset Icon */}
      {showResetIcon && !disabled && (
        <span
          className="material-icons material-symbols-outlined date_reset"
          onClick={(e) => {
            e.stopPropagation();
            onReset?.();
          }}
          style={{
            position: "absolute",
            right: "35px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            color: "#6c757d",
          }}
        >
          cached
        </span>
      )}

      {/* Calendar Icon */}
      <span
        className="material-icons material-symbols-outlined date_ic"
        onClick={!disabled ? onClick : undefined}
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: !disabled ? "pointer" : "not-allowed",
          color: "#6c757d",
        }}
      >
        calendar_month
      </span>

      {touched && error && <div className="invalid-feedback">{error}</div>}
    </div>
  ));

  CustomInput.displayName = "CustomInput";

  return (
    <Form.Group>
      <DatePicker
        selected={startDate}
        onChange={(update) => onChange(update as [Date | null, Date | null])}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        monthsShown={2}
        minDate={minDate}
        maxDate={maxDate}
        customInput={<CustomInput />}
        disabled={disabled}
        dateFormat="MM/dd/yyyy"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        yearDropdownItemNumber={15}
      />
    </Form.Group>
  );
};

export default DateRangePicker;
